import {handleActions, Action} from 'redux-actions';
import {format} from 'date-fns';

import {formatStatusTitle} from '@pluto/utils';
import {
    QCJobItem,
    QCJobsListPayload,
    QCManagerState,
    QCSummaryResponse,
    QCJobItemModified,
    QCSummaryUpdatedAction,
} from '../QCManagerTypes';
import {
    QC_JOBS_LIST_SET,
    QC_JOBS_LIST_RESET,
    QC_WORK_QUEUE_SET,
    QC_WORK_QUEUE_RESET,
    QC_JOBS_SUMMARY_SET,
    QC_WORK_QUEUE_SUMMARY_SET,
    QC_SUMMARY_UPDATE,
} from './QCManagerActions';
import {curry} from 'ramda';

export interface ContentItemNumber {
    episode?: number;
    season?: number;
}

// export const getFormattedDueDate = (date: string) => {
//     const startDateValue = new Date(date);
//     const dateValue = startDateValue.getDate();
//     const hoursValue = startDateValue.getHours();

//     return `${dateValue} at ${}`;
// };

export const checkForNullAndFormat = (date: string | null, type = 'Pp') =>
    date === null ? '' : format(new Date(date), type);

export const getContentItemNumber = (row: QCJobItem): ContentItemNumber => {
    const {contentItem} = row;
    const {number, seasonNumber} = contentItem;

    return {
        episode: number,
        season: seasonNumber,
    };
};

export const getStatusLabel = (status: string | undefined) => {
    if (status) return formatStatusTitle(status);

    return status;
};

export const getTitle = (
    seriesTitle?: string,
    title?: string,
    season?: number,
    episode?: number
) => {
    if (seriesTitle && season && episode)
        return `${seriesTitle} - S${season} - E${episode}`;

    if (title) return title;

    return seriesTitle;
};

export const formatRow = (row: QCJobItem) => {
    const {
        contentItem,
        priority,
        status,
        assignee,
        dueDate: rawDue,
        startDate: rawStartDate,
        finishDate: rawFinishDate,
    } = row;

    const {seriesTitle, title, contentPartner, contentItemType, labPartner} = contentItem;

    const {season, episode} = getContentItemNumber(row);
    const dueDate = checkForNullAndFormat(rawDue);
    const finishDate = checkForNullAndFormat(rawFinishDate);
    const startDate = checkForNullAndFormat(rawStartDate);
    const consolidatedTitle = getTitle(seriesTitle, title, season, episode);

    return {
        ...row,
        title: consolidatedTitle,
        season,
        episode,
        priority,
        dueDate,
        finishDate,
        startDate,
        labPartner,
        contentPartner: contentPartner.partnerName,
        contentType: contentItemType,
        assigneeName: assignee?.name,
        status: getStatusLabel(status),
    };
};

export const setItems = (
    state: QCManagerState,
    action: Action<QCJobsListPayload>
): QCManagerState => {
    const {rows, total} = action.payload;

    if (rows)
        return {
            ...state,
            rows: rows.map(formatRow as any),
            total,
        };

    return state;
};

export const setSummary = (
    state: QCManagerState,
    action: Action<QCSummaryResponse>
): QCManagerState => {
    const {READY_FOR_QC = 0, IN_QC = 0, QC_PASSED = 0, QC_FAILED = 0} = action.payload;

    return {
        ...state,
        READY_FOR_QC,
        IN_QC,
        QC_PASSED,
        QC_FAILED,
    };
};

export const resetItems = (): QCManagerState => {
    return initialState;
};

export const findRow = curry((id: number, row: QCJobItemModified) => row.id === id);

export const updateQCSummary = (state: QCManagerState, action: Action<QCSummaryUpdatedAction[]>) => {
    const {payload: [updatedItem]} = action;
    const {id, status} = updatedItem;

    const prevStatusName = state.rows.find(findRow(id))?.status?.replaceAll(' ', '_')?.toUpperCase() || '';
    const newStatus = state[status] as number;
    const prevStatus  = state[prevStatusName] as number;

    return {
        ...state,
        [status]: newStatus + 1,
        [prevStatusName]: prevStatus > 0 ? prevStatus - 1 : prevStatus,
    }
}

const initialState: QCManagerState = {
    rows: [],
    total: 0,
    READY_FOR_QC: 0,
    IN_QC: 0,
    QC_PASSED: 0,
    QC_FAILED: 0,
};

const handlers = {
    [QC_JOBS_LIST_SET]: setItems,
    [QC_JOBS_SUMMARY_SET]: setSummary,
    [QC_WORK_QUEUE_SUMMARY_SET]: setSummary,
    [QC_WORK_QUEUE_SET]: setItems,
    [QC_WORK_QUEUE_RESET]: resetItems,
    [QC_JOBS_LIST_RESET]: resetItems,
    [QC_SUMMARY_UPDATE]: updateQCSummary,
};

export default handleActions<QCManagerState, any>(handlers, initialState);
